import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { CalendarEvent } from "@compass/shared/types";

dayjs.extend(utc);
dayjs.extend(timezone);

export type CalendarFriendlyEvent = Omit<CalendarEvent, "end"> & {
    end: Date;
};

export const formatDate = (date: string, keepTime = false) => {
    const d = new Date(date);
    if (keepTime) return d;

    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

export const dayToDisplay = (date: Date, timeZone: string) => {
    return dayjs(date).tz(timeZone).format("MMMM D"); // "January 10"
};

export const dateRangeToDisplay = (
    start: string | Date | null | undefined,
    end: string | Date | null | undefined,
    timeZone: string,
) => {
    if (!start) return "";
    const startDate = new Date(start);
    const fmt = (d: Date) => dayjs(d).tz(timeZone).format("dddd MMM. D, YYYY");
    if (!end) return fmt(startDate);

    const endDate = new Date(end);
    const s = dayjs(startDate).tz(timeZone);
    const e = dayjs(endDate).tz(timeZone);
    if (s.isSame(e, "day")) {
        return fmt(startDate);
    }

    return `${fmt(startDate)} - ${fmt(endDate)}`;
};

/**
 * Two-line representation of an event's date/time. The consumer always renders
 * `primary` (loud) and optional `secondary` (quiet) — the structure is the
 * same shape regardless of single-day vs. multi-day, so the renderer doesn't
 * have to switch on `multiDay`. The flag is exposed for style hints only.
 */
export type DateTimeRangeParts = {
    primary: string;
    secondary: string | null;
    multiDay: boolean;
};

export const dateTimeRangeParts = (
    start: string | Date | null | undefined,
    end: string | Date | null | undefined,
    timeZone: string,
): DateTimeRangeParts | null => {
    if (!start) return null;

    const s = dayjs(start).tz(timeZone);
    const e = end ? dayjs(end).tz(timeZone) : null;

    const dateFmt = "dddd, MMM D, YYYY";
    const timeFmt = "h:mm A";
    const fullFmt = "MMM D, YYYY h:mm A";

    if (!e) {
        return { primary: s.format(dateFmt), secondary: s.format(timeFmt), multiDay: false };
    }

    if (s.isSame(e, "day")) {
        return {
            primary: s.format(dateFmt),
            secondary: `${s.format(timeFmt)} – ${e.format(timeFmt)}`,
            multiDay: false,
        };
    }

    return {
        primary: s.format(fullFmt),
        secondary: `– ${e.format(fullFmt)}`,
        multiDay: true,
    };
};

export const dateToDisplay = (date: Date, timeZone: string) =>
    dayjs(date).tz(timeZone).format("MM/DD/YY");
export const timeToDisplay = (date: Date, timeZone: string) =>
    dayjs(date).tz(timeZone).format("h:mm A");
